import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, ElementRef, HostListener, ViewChild, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, firstValueFrom, fromEvent } from 'rxjs';

import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import { AppPreferencesService } from '../../../services/app-preferences';
import { Httpservice } from '../../../services/httpservice';
import { ReportShellComponent } from '../../structure/report-shell/report-shell';
import { getTodayDateValue } from './daily-report.data';
import { DailyReportColumn, DailyReportGeneratorOption, DailyReportResponse, DailyReportRow, DailyReportTab } from './daily-report.interfaces';
import { ManageGeneratorAutocompleteComponent, ManageGeneratorAutocompleteCompleteEvent } from '../../master/manage-generator/manage-generator-autocomplete';
import {
  extractReportDateConversion,
  extractReportBsCalendar,
  getReportHierarchyNodeOrderKey,
  isOutsideElement,
  ReportBsCalendarCellLike,
  ReportBsCalendarLike,
  slugifyReportLabel,
  stripTotalReportLabel,
  stripTrailingParenthesizedText,
} from '../shared/report-ui.helpers';

interface DailyReportHierarchyNode {
  row: DailyReportRow;
  children: DailyReportHierarchyNode[];
}

interface DailyReportHierarchySubGroup {
  label: string;
  rows: DailyReportRow[];
}

interface DailyReportHierarchySourceGroup {
  label: string;
  subGroups: Map<string, DailyReportHierarchySubGroup>;
  directRows: DailyReportRow[];
}

interface DailyReportHierarchySectorGroup {
  label: string;
  sourceGroups: Map<string, DailyReportHierarchySourceGroup>;
  footerRows: DailyReportRow[];
}

interface DailyReportSectionTwoRow {
  label: string;
  highlighted: boolean;
  sourceRowKey?: string;
  rowKey?: string;
  expandable?: boolean;
  child?: boolean;
}

interface DailyReportSummaryTableRow {
  label: string;
  rowKey?: string;
  rowKeyIncludes?: string;
  labelIncludes?: string;
  kind?: 'normal' | 'time' | 'separator' | 'pink' | 'water-header' | 'water-level' | 'transmission';
}

type DailyReportSummaryColumn = 'peakDate' | 'peakLastYear' | 'energyDate' | 'energyLastYear' | 'average';

const DAILY_SUMMARY_ACCORDION_ORDER = [
  'sector:nea',
  'row:power-total-thermal',
  'sector:nea-subsidiary',
  'sector:ipp',
  'row:power-total-import',
  'row:power-total-export',
  'row:daily-generation-national-grid-only',
  'row:daily-total-supply-with-import',
  'row:power-net-import',
  'row:power-system-load-actual',
  'row:power-interruption-tripping',
  'row:power-system-load-expected',
  'row:power-national-load-actual',
  'row:power-frequency-voltage-profile',
  'row:power-frequency-voltage-substation',
  'row:power-kabeli-corridor',
  'row:power-kathmandu-load',
  'row:power-spill',
  'row:power-water-level',
] as const;
const DAILY_SUMMARY_ACCORDION_ORDER_MAP = new Map<string, number>(
  DAILY_SUMMARY_ACCORDION_ORDER.map((key, index) => [key, index]),
);

@Component({
  selector: 'app-daily-report-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ReportShellComponent, ManageGeneratorAutocompleteComponent],
  templateUrl: './daily-report.html',
  styleUrl: './daily-report.scss',
})
export class DailyReportPage implements OnInit {
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly filtersForm: UntypedFormGroup;
  protected readonly tabs: { key: DailyReportTab; label: string }[] = [
    { key: 'section-1', label: 'Section 1' },
    { key: 'section-2', label: 'Section 2' },
    { key: 'summary', label: 'Summary' },
  ];
  protected readonly sectionTwoColumns = ['Max.', 'Min.', 'Peak'];
  protected readonly sectionTwoRows: DailyReportSectionTwoRow[] = [
    { label: 'Frequency', highlighted: true, sourceRowKey: 'power-frequency' },
    { label: 'KTM Valley (MW)', highlighted: true, sourceRowKey: 'power-kathmandu-load' },
    { label: 'Temp', highlighted: true },
    { label: 'KTM', highlighted: false },
    { label: 'TERAI', highlighted: false },
    { label: 'Line', highlighted: true, rowKey: 'section-two-line-flows', expandable: true },
    { label: 'Nat. Peak', highlighted: false, sourceRowKey: 'power-national-load-actual' },
    { label: 'Syst. Peak', highlighted: false, sourceRowKey: 'power-system-load-expected' },
  ];
  protected readonly baseSummaryTableRows: DailyReportSummaryTableRow[] = [
    { label: 'NEA', rowKey: 'sector-total-nea' },
    { label: 'NEA SUBSIDIARY', rowKey: 'sector-total-nea-subsidiary' },
    { label: 'IPP', rowKey: 'sector-total-ipp' },
    { label: 'ROR Generation', rowKeyIncludes: 'subcat-total', labelIncludes: 'total ror' },
    { label: 'Import', rowKey: 'power-total-import' },
    { label: 'Interruption', rowKey: 'power-interruption-tripping' },
    { label: 'Export', rowKey: 'power-total-export' },
    { label: 'System Peak Demand', rowKey: 'power-system-load-expected' },
    { label: 'National Peak Demand', rowKey: 'power-national-load-actual' },
    { label: 'UTK' },
    { label: 'Total Solar', rowKeyIncludes: 'source-total', labelIncludes: 'total solar' },
    { label: 'NEA Storage Energy', rowKeyIncludes: 'subcat-total', labelIncludes: 'total storage' },
    { label: '', kind: 'separator' },
    { label: 'National Base Load (MW)' },
    { label: 'System Base Load (MW)' },
    { label: 'KL-I Water Level (m)', rowKey: 'power-water-level', kind: 'water-header' },
    { label: 'Kathmandu Peak Load (MW)', rowKey: 'power-kathmandu-load' },
    { label: 'Present Max.Export (MW):', rowKey: 'power-total-export', kind: 'pink' },
    { label: 'Present Max.Import (MW):', rowKey: 'power-total-import', kind: 'pink' },
    { label: 'Bangladesh Ex. From DN (MWh):', kind: 'pink' },
  ];
  protected selectedTab: DailyReportTab = 'section-1';
  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;
  protected columns: DailyReportColumn[] = [];
  protected rows: DailyReportRow[] = [];
  protected summarySourceRows: DailyReportRow[] = [];
  protected lastYearSummarySourceRows: DailyReportRow[] = [];
  protected yesterdaySummarySourceRows: DailyReportRow[] = [];
  protected boySummarySourceRows: DailyReportRow[] = [];
  protected generatorOptions: DailyReportGeneratorOption[] = [];
  protected filteredGeneratorOptions: DailyReportGeneratorOption[] = [];
  protected generatorSearchTerm = '';
  protected selectedDateDisplayLabel = getTodayDateValue();
  protected bsCalendarOpen = false;
  protected bsCalendarLoading = false;
  protected bsCalendarMonth: ReportBsCalendarLike | null = null;
  protected readonly bsCalendarWeekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  @ViewChild('hiddenDateInput') private readonly hiddenDateInput?: ElementRef<HTMLInputElement>;
  @ViewChild('bsDatePickerShell') private readonly bsDatePickerShell?: ElementRef<HTMLDivElement>;

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private reportRowsCache: DailyReportRow[] = [];
  private expandedRowKeys = new Set<string>();
  private readonly sectionTwoExpandedRowKeys = new Set<string>();
  private selectedDateRequestValue = getTodayDateValue();

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly httpSvc: Httpservice,
    private readonly preferencesSvc: AppPreferencesService,
  ) {
    this.filtersForm = this.fb.group({
      date: [getTodayDateValue(), Validators.required],
      substation_id: [null],
    });
  }

  ngOnInit(): void {
    this.filtersForm.get('date')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.handleDateSelectionChange();
      });
    this.filtersForm.get('substation_id')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadReport());
    this.listenToPreferenceChanges();
    this.loadGeneratorOptions();
    this.handleDateSelectionChange();
  }

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }

    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected get isBsDateMode(): boolean {
    return this.preferencesSvc.showBsDate;
  }

  @HostListener('document:mousedown', ['$event'])
  protected handleDocumentMouseDown(event: MouseEvent): void {
    if (!this.bsCalendarOpen) {
      return;
    }

    if (!isOutsideElement(event.target, this.bsDatePickerShell?.nativeElement)) {
      return;
    }

    this.closeBsDatePicker();
  }

  protected openDatePicker(): void {
    if (this.isBsDateMode) {
      if (this.bsCalendarOpen) {
        this.closeBsDatePicker();
        return;
      }

      this.openBsDatePicker();
      return;
    }

    const input = this.hiddenDateInput?.nativeElement;
    if (!input) {
      return;
    }
    if (typeof input.showPicker === 'function') {
      input.showPicker();
      return;
    }
    input.click();
  }

  protected closeBsDatePicker(): void {
    this.bsCalendarOpen = false;
  }

  protected selectBsCalendarCell(cell: ReportBsCalendarCellLike): void {
    if (!cell?.ad_date) {
      return;
    }

    this.closeBsDatePicker();
    this.filtersForm.patchValue({ date: cell.ad_date }, { emitEvent: true });
  }

  protected shiftBsCalendarMonth(offset: number): void {
    if (!this.bsCalendarMonth || !Number.isFinite(offset) || !offset) {
      return;
    }

    const nextMonth = this.bsCalendarMonth.bs_month + offset;
    let nextYear = this.bsCalendarMonth.bs_year;
    let normalizedMonth = nextMonth;

    if (nextMonth < 1) {
      normalizedMonth = 12;
      nextYear -= 1;
    } else if (nextMonth > 12) {
      normalizedMonth = 1;
      nextYear += 1;
    }

    this.loadBsCalendar(`${nextYear.toString().padStart(4, '0')}-${normalizedMonth.toString().padStart(2, '0')}-01`, 'BS');
  }

  protected setTab(tab: DailyReportTab): void {
    if (this.selectedTab === tab) {
      return;
    }

    this.selectedTab = tab;
    this.expandedRowKeys.clear();
    this.loadReport();
  }

  protected clearFilters(): void {
    this.generatorSearchTerm = '';
    this.filtersForm.patchValue({
      date: getTodayDateValue(),
      substation_id: null,
    });
  }

  protected onGeneratorSearchInput(event: ManageGeneratorAutocompleteCompleteEvent): void {
    this.filterGeneratorAutocomplete(event?.query ?? '');
  }

  protected onGeneratorSearchBlur(): void {
    this.commitGeneratorAutocompleteValue();
  }

  protected trackByRow(_: number, row: DailyReportRow): string {
    return row.row_key ?? `${row.row_kind ?? 'data'}-${row.station_id}`;
  }

  protected trackByColumn(_: number, column: DailyReportColumn): string {
    return column.key;
  }

  protected trackBySectionTwoColumn(_: number, column: string): string {
    return column;
  }

  protected trackBySectionTwoRow(_: number, row: DailyReportSectionTwoRow): string {
    return row.rowKey ?? row.sourceRowKey ?? row.label;
  }

  protected getSectionTwoRows(): DailyReportSectionTwoRow[] {
    const rows: DailyReportSectionTwoRow[] = [];
    for (const row of this.sectionTwoRows) {
      rows.push(row);
      if (row.rowKey === 'section-two-line-flows' && this.isSectionTwoRowExpanded(row)) {
        rows.push(...this.getLineFlowSectionTwoRows());
      }
    }
    return rows;
  }

  protected getSectionTwoCellText(row: DailyReportSectionTwoRow, column: string): string {
    if (!row.sourceRowKey) {
      return '';
    }

    const sourceRow = this.reportRowsCache.find((item) => item.row_key === row.sourceRowKey);
    if (this.isSectionTwoPeakRow(row)) {
      return this.getSectionTwoPeakRowCellText(sourceRow, column);
    }

    const value = this.getSectionTwoMetricValue(sourceRow, column);
    return value === null || value === undefined ? '' : this.formatNumber(value);
  }

  protected trackBySummaryTableRow(_: number, row: DailyReportSummaryTableRow): string {
    return `${row.kind ?? 'normal'}-${row.rowKey ?? (row.label || 'separator')}`;
  }

  protected getSummaryTableRows(): DailyReportSummaryTableRow[] {
    const rows: DailyReportSummaryTableRow[] = [];
    for (const row of this.baseSummaryTableRows) {
      rows.push(row);
      if (row.kind === 'water-header') {
        rows.push(...this.getWaterLevelSummaryRows());
      }
      if (row.rowKey === 'power-kathmandu-load') {
        rows.push(...this.getTransmissionSummaryRows());
      }
    }
    return rows;
  }

  protected getSummaryCellText(row: DailyReportSummaryTableRow, column: DailyReportSummaryColumn): string {
    if (row.kind === 'separator') {
      return '';
    }

    if (row.kind === 'water-header') {
      return this.getWaterHeaderCellText(column);
    }
    if (row.kind === 'water-level') {
      return this.getWaterLevelCellText(row, column);
    }

    const sourceRows = column === 'peakLastYear' || column === 'energyLastYear'
      ? this.lastYearSummarySourceRows
      : this.summarySourceRows;
    const sourceRow = this.findSummaryTableSourceRow(row, sourceRows);
    if (!sourceRow) {
      return '';
    }

    if (column === 'peakDate' || column === 'peakLastYear') {
      return this.formatNullableNumber(this.getSummaryValueAtNationalPeak(sourceRow, sourceRows));
    }
    if (column === 'energyDate' || column === 'energyLastYear') {
      return this.formatNullableNumber(this.normalizeNumber(sourceRow.daily_values?.['generation_mwh']));
    }
    if (column === 'average') {
      return this.formatNullableNumber(this.getRowAverageBlockValue(sourceRow));
    }

    return '';
  }

  protected getSummaryRowClass(row: DailyReportSummaryTableRow): string {
    const classes = ['daily-report-summary-table__row'];
    if (row.kind) {
      classes.push(`daily-report-summary-table__row--${row.kind}`);
    }
    classes.push(`daily-report-summary-table__row--${this.getSummaryRowColorKind(row)}`);
    return classes.join(' ');
  }

  protected getSummaryPeakTimeLabel(isLastYear: boolean): string {
    const peakTime = this.getNationalPeakTime(isLastYear ? this.lastYearSummarySourceRows : this.summarySourceRows);
    return peakTime ? `(${peakTime})` : '';
  }

  protected hasSectionTwoChildren(row: DailyReportSectionTwoRow): boolean {
    return Boolean(row.expandable && this.getLineFlowSectionTwoRows().length);
  }

  protected isSectionTwoRowExpanded(row: DailyReportSectionTwoRow): boolean {
    return Boolean(row.rowKey && this.sectionTwoExpandedRowKeys.has(row.rowKey));
  }

  protected toggleSectionTwoRow(row: DailyReportSectionTwoRow): void {
    if (!row.rowKey || !this.hasSectionTwoChildren(row)) {
      return;
    }

    if (this.sectionTwoExpandedRowKeys.has(row.rowKey)) {
      this.sectionTwoExpandedRowKeys.delete(row.rowKey);
    } else {
      this.sectionTwoExpandedRowKeys.add(row.rowKey);
    }
  }

  protected getCellText(row: DailyReportRow, column: DailyReportColumn): string {
    const value = row.daily_values?.[column.key];
    if (this.isBlankParentRow(row)) {
      return '';
    }

    return value === null || value === undefined ? '' : this.formatNumber(value);
  }

  protected hasHierarchyChildren(row: DailyReportRow): boolean {
    return Boolean(row.row_key && row.has_children);
  }

  protected isHierarchyExpanded(row: DailyReportRow): boolean {
    return Boolean(row.row_key && this.expandedRowKeys.has(row.row_key));
  }

  protected toggleHierarchyRow(row: DailyReportRow): void {
    if (!row.row_key || !row.has_children) {
      return;
    }

    if (this.expandedRowKeys.has(row.row_key)) {
      this.expandedRowKeys.delete(row.row_key);
    } else {
      this.expandedRowKeys.add(row.row_key);
    }
    this.rows = this.buildDisplayRows();
  }

  protected getEmptyColspan(): number {
    return this.columns.length + 1;
  }

  protected isBlankParentRow(row: DailyReportRow): boolean {
    return row.row_key === 'power-frequency-voltage-profile' || row.row_key === 'power-frequency-voltage-substation';
  }

  private getSectionTwoMetricValue(row: DailyReportRow | undefined, column: string): number | null {
    if (!row) {
      return null;
    }

    if (column === 'Max.') {
      return this.getRowMaximumBlock(row)?.value ?? row.daily_values?.['max_load_mw'] ?? null;
    }
    if (column === 'Min.') {
      return this.getRowMinimumBlockValue(row);
    }
    if (column === 'Peak') {
      return row.daily_values?.['load_at_system_peak_mw'] ?? null;
    }

    return null;
  }

  private isSectionTwoPeakRow(row: DailyReportSectionTwoRow): boolean {
    return row.sourceRowKey === 'power-national-load-actual' || row.sourceRowKey === 'power-system-load-expected';
  }

  private getSectionTwoPeakRowCellText(row: DailyReportRow | undefined, column: string): string {
    if (!row) {
      return '';
    }

    if (column === 'Min.') {
      return this.formatNullableNumber(this.getRowMinimumBlockValue(row));
    }

    const peakBlock = this.getRowMaximumBlock(row);
    if (!peakBlock) {
      return '';
    }

    if (column === 'Max.') {
      return this.formatNumber(peakBlock.value);
    }

    if (column === 'Peak') {
      return `${this.formatNumber(peakBlock.value)} (${peakBlock.label})`;
    }

    return '';
  }

  private getLineFlowSectionTwoRows(): DailyReportSectionTwoRow[] {
    return this.reportRowsCache
      .filter((row) => row.parent_row_key === 'crossborder-line-flows')
      .map((row) => ({
        label: row.station_name,
        highlighted: false,
        sourceRowKey: row.row_key,
        rowKey: `section-two-${row.row_key}`,
        child: true,
      }));
  }

  private getWaterLevelSummaryRows(): DailyReportSummaryTableRow[] {
    return this.summarySourceRows
      .filter((row) => row.parent_row_key === 'power-water-level')
      .map((row) => ({
        label: row.station_name,
        rowKey: row.row_key,
        kind: 'water-level',
      }));
  }

  private getTransmissionSummaryRows(): DailyReportSummaryTableRow[] {
    return this.summarySourceRows
      .filter((row) => row.parent_row_key === 'crossborder-line-flows')
      .map((row) => ({
        label: row.station_name,
        rowKey: row.row_key,
        kind: 'transmission',
      }));
  }

  private getWaterHeaderCellText(column: DailyReportSummaryColumn): string {
    if (column === 'peakDate') {
      return 'Level (m)';
    }
    if (column === 'peakLastYear') {
      return 'Change WRT Yesterday';
    }
    if (column === 'energyDate') {
      return 'Change WRT BOY';
    }

    return '';
  }

  private getWaterLevelCellText(row: DailyReportSummaryTableRow, column: DailyReportSummaryColumn): string {
    const currentRow = row.rowKey ? this.findSummaryRowByKey(this.summarySourceRows, row.rowKey) : null;
    if (!currentRow) {
      return '';
    }

    const currentValue = this.getSummaryRowLevelValue(currentRow);
    if (column === 'peakDate') {
      return this.formatNullableNumber(currentValue);
    }
    if (column === 'peakLastYear') {
      return this.formatNullableNumber(this.subtractNullable(currentValue, this.getComparisonLevelValue(row, this.yesterdaySummarySourceRows)));
    }
    if (column === 'energyDate') {
      return this.formatNullableNumber(this.subtractNullable(currentValue, this.getComparisonLevelValue(row, this.boySummarySourceRows)));
    }

    return '';
  }

  private getComparisonLevelValue(row: DailyReportSummaryTableRow, sourceRows: DailyReportRow[]): number | null {
    const comparisonRow = row.rowKey ? this.findSummaryRowByKey(sourceRows, row.rowKey) : null;
    return comparisonRow ? this.getSummaryRowLevelValue(comparisonRow) : null;
  }

  private getSummaryRowLevelValue(row: DailyReportRow): number | null {
    const values = row.blocks
      .map((block) => this.normalizeNumber(block.value))
      .filter((value): value is number => value !== null);
    if (!values.length) {
      return this.normalizeNumber(row.daily_values?.['generation_mwh']);
    }

    return values.reduce((sum, value) => sum + value, 0);
  }

  private subtractNullable(left: number | null, right: number | null): number | null {
    if (left === null || right === null) {
      return null;
    }

    return left - right;
  }

  private getRowMinimumBlockValue(row: DailyReportRow): number | null {
    const values = row.blocks
      .map((block) => this.normalizeNumber(block.value))
      .filter((value): value is number => value !== null);
    return values.length ? Math.min(...values) : null;
  }

  private getRowAverageBlockValue(row: DailyReportRow): number | null {
    const values = row.blocks
      .map((block) => this.normalizeNumber(block.value))
      .filter((value): value is number => value !== null);
    if (!values.length) {
      return null;
    }

    return values.reduce((sum, value) => sum + value, 0) / values.length;
  }

  private getRowMaximumBlock(row: DailyReportRow): { value: number; label: string; blockNo: number } | null {
    return row.blocks.reduce<{ value: number; label: string; blockNo: number } | null>((maxBlock, block) => {
      const value = this.normalizeNumber(block.value);
      if (value === null) {
        return maxBlock;
      }
      if (!maxBlock || value > maxBlock.value) {
        return { value, label: block.label, blockNo: block.block_no };
      }
      return maxBlock;
    }, null);
  }

  private formatNullableNumber(value: number | null): string {
    return value === null ? '' : this.formatNumber(value);
  }

  private getSummaryValueAtNationalPeak(row: DailyReportRow, rows: DailyReportRow[]): number | null {
    const peakBlockNo = this.getNationalPeakBlock(rows)?.blockNo;
    if (peakBlockNo === undefined) {
      return null;
    }

    const block = row.blocks.find((item) => item.block_no === peakBlockNo);
    return block ? this.normalizeNumber(block.value) : null;
  }

  private getNationalPeakTime(rows: DailyReportRow[]): string {
    return this.getNationalPeakBlock(rows)?.label ?? '';
  }

  private getNationalPeakBlock(rows: DailyReportRow[]): { value: number; label: string; blockNo: number } | null {
    const nationalPeakRow = this.findSummaryRowByKey(rows, 'power-national-load-actual');
    return nationalPeakRow ? this.getRowMaximumBlock(nationalPeakRow) : null;
  }

  private getSummaryRowColorKind(config: DailyReportSummaryTableRow): string {
    if (config.kind === 'separator' || config.kind === 'pink') {
      return config.kind;
    }

    const sourceRow = this.findSummaryTableSourceRow(config, this.summarySourceRows);
    const rowKind = sourceRow?.row_kind ?? '';
    const rowKey = sourceRow?.row_key ?? '';
    if (rowKind === 'sector-total' || this.isGreenSummaryRowKey(rowKey)) {
      return 'section-green';
    }
    if (rowKind === 'source-total' || this.isBlueSummaryRowKey(rowKey)) {
      return 'section-blue';
    }
    if (rowKind === 'subcat-total') {
      return 'section-yellow';
    }

    return 'section-data';
  }

  private isGreenSummaryRowKey(rowKey: string): boolean {
    return [
      'power-total-import',
      'power-total-export',
      'power-system-load-actual',
      'power-system-load-expected',
      'power-national-load-actual',
    ].includes(rowKey);
  }

  private isBlueSummaryRowKey(rowKey: string): boolean {
    return [
      'power-water-level',
      'power-spill',
      'power-kabeli-corridor',
      'power-frequency-voltage-profile',
      'power-frequency-voltage-substation',
    ].includes(rowKey);
  }

  private findSummaryTableSourceRow(config: DailyReportSummaryTableRow, rows: DailyReportRow[]): DailyReportRow | null {
    if (config.rowKey) {
      return this.findSummaryRowByKey(rows, config.rowKey);
    }

    const normalizedLabel = config.labelIncludes?.toLowerCase();
    return rows.find((row) => {
      const rowKey = (row.row_key ?? '').toLowerCase();
      const stationName = row.station_name.toLowerCase();
      const keyMatches = !config.rowKeyIncludes || rowKey.includes(config.rowKeyIncludes);
      const labelMatches = !normalizedLabel || stationName.includes(normalizedLabel);
      return keyMatches && labelMatches;
    }) ?? null;
  }

  private findSummaryRowByKey(rows: DailyReportRow[], rowKey: string): DailyReportRow | null {
    return rows.find((row) => row.row_key === rowKey) ?? null;
  }

  private getLastYearDateValue(dateValue: string): string {
    const [year, month, day] = dateValue.split('-').map((part) => Number(part));
    if (!year || !month || !day) {
      return dateValue;
    }

    const lastYearDate = new Date(year - 1, month - 1, day);
    return [
      lastYearDate.getFullYear(),
      String(lastYearDate.getMonth() + 1).padStart(2, '0'),
      String(lastYearDate.getDate()).padStart(2, '0'),
    ].join('-');
  }

  private getYesterdayDateValue(dateValue: string): string {
    return this.shiftDateValue(dateValue, -1);
  }

  private getBeginningOfYearDateValue(dateValue: string): string {
    const [year] = dateValue.split('-').map((part) => Number(part));
    return year ? `${year}-01-01` : dateValue;
  }

  private shiftDateValue(dateValue: string, dayOffset: number): string {
    const [year, month, day] = dateValue.split('-').map((part) => Number(part));
    if (!year || !month || !day) {
      return dateValue;
    }

    const shiftedDate = new Date(year, month - 1, day + dayOffset);
    return [
      shiftedDate.getFullYear(),
      String(shiftedDate.getMonth() + 1).padStart(2, '0'),
      String(shiftedDate.getDate()).padStart(2, '0'),
    ].join('-');
  }

  private getDatePreferenceKey(): 'AD' | 'BS' {
    return this.preferencesSvc.showBsDate ? 'BS' : 'AD';
  }

  private getSelectedDateValue(): string {
    return this.selectedDateRequestValue || this.getSelectedDateFormValue();
  }

  private getSelectedDateFormValue(): string {
    const value = this.filtersForm.get('date')?.value;
    return typeof value === 'string' && value ? value : getTodayDateValue();
  }

  private listenToPreferenceChanges(): void {
    if (typeof window === 'undefined') {
      return;
    }

    fromEvent<CustomEvent<{ name?: string }>>(window, 'app-preferences-change')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.detail?.name !== 'showBsDate') {
          return;
        }

        this.closeBsDatePicker();
        this.handleDateSelectionChange();
      });
  }

  private async openBsDatePicker(): Promise<void> {
    this.bsCalendarOpen = true;
    await this.loadBsCalendar(this.getSelectedDateValue() || this.getSelectedDateFormValue(), this.getDatePreferenceKey());
  }

  private async loadBsCalendar(bsDateValue: string, bsAdKey: 'AD' | 'BS' = 'BS'): Promise<void> {
    if (!bsDateValue) {
      return;
    }

    this.bsCalendarLoading = true;
    try {
      const response = await firstValueFrom(
        this.httpSvc.requestCall(
          AuthEndPoints.GET_LOG_SHEET_BS_CALENDAR,
          ApiMethod.POST,
          {
            data_date: bsDateValue,
            bs_ad_key: bsAdKey,
          },
          false,
          { suppressToast: true },
        ),
      );
      this.bsCalendarMonth = extractReportBsCalendar(response);
    } catch {
      this.bsCalendarMonth = null;
    } finally {
      this.bsCalendarLoading = false;
      this.cdr.detectChanges();
    }
  }

  private async handleDateSelectionChange(): Promise<void> {
    const adDate = this.getSelectedDateFormValue();

    if (this.preferencesSvc.showBsDate) {
      await this.syncBsDateSelection(adDate);
    } else {
      this.selectedDateRequestValue = adDate;
      this.selectedDateDisplayLabel = this.preferencesSvc.formatDateOnly(adDate);
    }

    this.loadReport();
  }

  private async syncBsDateSelection(adDate: string): Promise<void> {
    if (!adDate) {
      this.selectedDateRequestValue = getTodayDateValue();
      this.selectedDateDisplayLabel = getTodayDateValue();
      return;
    }

    try {
      const response = await firstValueFrom(
        this.httpSvc.requestCall(
          AuthEndPoints.CONVERT_LOG_SHEET_DATE,
          ApiMethod.POST,
          {
            data_date: adDate,
            bs_ad_key: 'BS',
          },
          false,
          { suppressToast: true },
        ),
      );
      const converted = extractReportDateConversion(response);
      if (converted) {
        this.selectedDateRequestValue = converted.selected_date || converted.bs_date;
        this.selectedDateDisplayLabel = converted.selected_date_label || converted.bs_date_label;
        return;
      }
    } catch {
      // Keep AD date as a safe fallback.
    }

    this.selectedDateRequestValue = adDate;
    this.selectedDateDisplayLabel = this.preferencesSvc.formatDateOnly(adDate);
  }

  private loadReport(): void {
    this.isLoading = true;
    this.lastYearSummarySourceRows = [];
    this.yesterdaySummarySourceRows = [];
    this.boySummarySourceRows = [];
    const payload = {
      data_date: this.getSelectedDateValue(),
      generation_id: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
      substation_id: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
      section: this.selectedTab,
      bs_ad_key: this.getDatePreferenceKey(),
    };
    this.httpSvc
      .requestCall(AuthEndPoints.GET_DAILY_REPORT, ApiMethod.POST, payload, false, { suppressToast: true })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.applyReport(this.extractReport(response));
          if (this.selectedTab === 'summary') {
            this.loadSummaryComparisonReports();
          }
        },
        error: () => this.applyReport(null),
      });
  }

  private loadSummaryComparisonReports(): void {
    const selectedDate = this.getSelectedDateFormValue();
    this.loadSummaryComparisonReport(this.getLastYearDateValue(selectedDate), (rows) => {
      this.lastYearSummarySourceRows = rows;
    });
    this.loadSummaryComparisonReport(this.getYesterdayDateValue(selectedDate), (rows) => {
      this.yesterdaySummarySourceRows = rows;
    });
    this.loadSummaryComparisonReport(this.getBeginningOfYearDateValue(selectedDate), (rows) => {
      this.boySummarySourceRows = rows;
    });
  }

  private loadSummaryComparisonReport(dateValue: string, assignRows: (rows: DailyReportRow[]) => void): void {
    const payload = {
      data_date: dateValue,
      generation_id: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
      substation_id: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
      section: 'summary',
      bs_ad_key: 'AD',
    };
    this.httpSvc
      .requestCall(AuthEndPoints.GET_DAILY_REPORT, ApiMethod.POST, payload, false, { suppressToast: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const report = this.extractReport(response);
          assignRows(report ? this.flattenAllNodes(this.buildRootNodes(report.rows)) : []);
          this.cdr.markForCheck();
        },
        error: () => {
          assignRows([]);
          this.cdr.markForCheck();
        },
      });
  }

  private loadGeneratorOptions(): void {
    this.httpSvc
      .requestCall(AuthEndPoints.GET_GENERATORS_LIST, ApiMethod.GET, '', false, { suppressToast: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.generatorOptions = this.extractGeneratorOptions(response);
          this.filteredGeneratorOptions = [...this.generatorOptions];
          this.cdr.markForCheck();
        },
        error: () => {
          this.generatorOptions = [];
          this.filteredGeneratorOptions = [];
          this.cdr.markForCheck();
        },
      });
  }

  private filterGeneratorAutocomplete(query: string): void {
    const normalizedQuery = typeof query === 'string' ? query.trim().toLowerCase() : '';
    if (!normalizedQuery) {
      this.filteredGeneratorOptions = [...this.generatorOptions];
      return;
    }

    this.filteredGeneratorOptions = this.generatorOptions.filter((option) => {
      const name = option.name.toLowerCase();
      const code = (option.station_code ?? '').toLowerCase();
      return name.includes(normalizedQuery) || code.includes(normalizedQuery);
    });
  }

  private commitGeneratorAutocompleteValue(): void {
    const currentValue = typeof this.generatorSearchTerm === 'string' ? this.generatorSearchTerm.trim() : '';
    if (!currentValue) {
      this.filtersForm.patchValue({ substation_id: null });
      return;
    }

    const match = this.findGeneratorByLabel(currentValue);
    if (!match) {
      this.generatorSearchTerm = '';
      this.filtersForm.patchValue({ substation_id: null });
      return;
    }

    this.generatorSearchTerm = match.name;
    this.filtersForm.patchValue({ substation_id: match.id });
  }

  private findGeneratorByLabel(label: string): DailyReportGeneratorOption | null {
    const normalizedLabel = label.trim().toLowerCase();
    if (!normalizedLabel) {
      return null;
    }

    return this.generatorOptions.find((option) => option.name.trim().toLowerCase() === normalizedLabel) ?? null;
  }

  private extractGeneratorOptions(response: unknown): DailyReportGeneratorOption[] {
    const value = this.extractResponseValue(response);
    const rows = this.getArray(this.getRecord(value)['generators']);
    return rows.map((item) => {
      const record = this.getRecord(item);
      const generatorName = this.extractString(record['generator_name']) ?? this.extractString(record['name']) ?? '-';
      return {
        id: this.normalizeNumber(record['id']) ?? 0,
        name: generatorName,
        station_code: this.extractString(record['scada_station_code']) ?? this.extractString(record['station_code']),
      };
    });
  }

  private applyReport(report: DailyReportResponse | null): void {
    if (!report) {
      this.columns = [];
      this.rows = [];
      this.reportRowsCache = [];
      this.summarySourceRows = [];
      return;
    }

    this.columns = report.columns;
    if (!this.preferencesSvc.showBsDate && report.date_label) {
      this.selectedDateDisplayLabel = report.date_label;
    }
    this.reportRowsCache = report.rows.map((row) => ({ ...row, blocks: row.blocks.map((block) => ({ ...block })) }));
    const rootNodes = this.buildRootNodes();
    this.summarySourceRows = this.flattenAllNodes(rootNodes);
    this.expandedRowKeys = this.getDefaultExpandedRowKeys(rootNodes);
    this.rows = this.flattenNodes(rootNodes);
  }

  private buildDisplayRows(): DailyReportRow[] {
    const roots = this.buildRootNodes();
    return this.flattenNodes(roots);
  }

  private buildRootNodes(sourceRows = this.reportRowsCache): DailyReportHierarchyNode[] {
    const hierarchyRows = sourceRows.filter((row) => !row.force_flat);
    const flatRows = sourceRows.filter((row) => row.force_flat);
    return this.sortNodes([
      ...this.buildHierarchyNodes(hierarchyRows),
      ...this.buildLinkedRowNodes(flatRows),
    ]);
  }

  private buildHierarchyNodes(rows: DailyReportRow[]): DailyReportHierarchyNode[] {
    const normalizedRows = rows.map((row) => ({
      ...row,
      row_kind: row.row_kind === 'power-small' || row.parent_row_key ? row.row_kind : 'data',
      row_level: row.row_kind === 'power-small' || row.parent_row_key ? row.row_level : 3,
      row_key: row.row_kind === 'power-small' || row.parent_row_key ? row.row_key : `data-${row.station_id}`,
      has_children: false,
    }));
    const { sectorGroupMap, sectorLabels } = this.groupHierarchyRows(normalizedRows);
    const sectorNodes: DailyReportHierarchyNode[] = [];
    for (const sectorLabel of sectorLabels) {
      const sectorGroup = sectorGroupMap.get(sectorLabel);
      if (sectorGroup) {
        sectorNodes.push(this.buildHierarchySectorNode(sectorLabel, sectorGroup));
      }
    }

    return this.sortNodes(sectorNodes);
  }

  private groupHierarchyRows(rows: DailyReportRow[]): {
    sectorGroupMap: Map<string, DailyReportHierarchySectorGroup>;
    sectorLabels: string[];
  } {
    const sectorGroupMap = new Map<string, DailyReportHierarchySectorGroup>();
    const sectorLabels: string[] = [];
    for (const row of rows) {
      const sectorLabel = this.normalizeHierarchyLabel(row.owner);
      const sourceLabel = this.normalizeHierarchyLabel(row.source_category);
      const subLabel = this.normalizeOptionalHierarchyLabel(row.sub_category);
      const sectorGroup = this.getOrCreateSectorGroup(sectorGroupMap, sectorLabels, sectorLabel);
      if (row.row_kind === 'power-small' || row.parent_row_key) {
        sectorGroup.footerRows.push(row);
        continue;
      }

      const sourceGroup = this.getOrCreateSourceGroup(sectorGroup, sourceLabel);
      if (subLabel) {
        this.getOrCreateSubGroup(sourceGroup, subLabel).rows.push(row);
      } else {
        sourceGroup.directRows.push(row);
      }
    }

    return { sectorGroupMap, sectorLabels };
  }

  private getOrCreateSectorGroup(
    sectorGroupMap: Map<string, DailyReportHierarchySectorGroup>,
    sectorLabels: string[],
    sectorLabel: string,
  ): DailyReportHierarchySectorGroup {
    const existingGroup = sectorGroupMap.get(sectorLabel);
    if (existingGroup) {
      return existingGroup;
    }

    const newGroup = {
      label: sectorLabel,
      sourceGroups: new Map<string, DailyReportHierarchySourceGroup>(),
      footerRows: [],
    };
    sectorGroupMap.set(sectorLabel, newGroup);
    sectorLabels.push(sectorLabel);
    return newGroup;
  }

  private getOrCreateSourceGroup(
    sectorGroup: DailyReportHierarchySectorGroup,
    sourceLabel: string,
  ): DailyReportHierarchySourceGroup {
    const existingGroup = sectorGroup.sourceGroups.get(sourceLabel);
    if (existingGroup) {
      return existingGroup;
    }

    const newGroup = {
      label: sourceLabel,
      subGroups: new Map<string, DailyReportHierarchySubGroup>(),
      directRows: [],
    };
    sectorGroup.sourceGroups.set(sourceLabel, newGroup);
    return newGroup;
  }

  private getOrCreateSubGroup(
    sourceGroup: DailyReportHierarchySourceGroup,
    subLabel: string,
  ): DailyReportHierarchySubGroup {
    const existingGroup = sourceGroup.subGroups.get(subLabel);
    if (existingGroup) {
      return existingGroup;
    }

    const newGroup = { label: subLabel, rows: [] };
    sourceGroup.subGroups.set(subLabel, newGroup);
    return newGroup;
  }

  private buildHierarchySectorNode(sectorLabel: string, sectorGroup: DailyReportHierarchySectorGroup): DailyReportHierarchyNode {
    const sourceNodes = Array.from(sectorGroup.sourceGroups.entries()).map(([sourceLabel, sourceGroup]) =>
      this.buildHierarchySourceNode(sectorLabel, sourceLabel, sourceGroup),
    );
    const footerNodes = this.buildLinkedRowNodes(sectorGroup.footerRows);
    const sectorRows = [
      ...Array.from(sectorGroup.sourceGroups.values()).flatMap((group) => [
        ...group.directRows,
        ...Array.from(group.subGroups.values()).flatMap((subGroup) => subGroup.rows),
      ]),
      ...sectorGroup.footerRows.filter((row) => !row.parent_row_key && row.row_kind !== 'power-small'),
    ];
    return {
      row: this.buildAggregateRow(
        `sector-total-${this.slugify(sectorLabel)}`,
        `Total ${sectorLabel}`,
        sectorRows,
        0,
        'sector-total',
        true,
        true,
      ),
      children: [...sourceNodes, ...footerNodes],
    };
  }

  private buildHierarchySourceNode(
    sectorLabel: string,
    sourceLabel: string,
    sourceGroup: DailyReportHierarchySourceGroup,
  ): DailyReportHierarchyNode {
    const subGroups = Array.from(sourceGroup.subGroups.entries());
    const fuelSubGroups = subGroups.filter(([, group]) => this.isFuelHierarchyLabel(group.label));
    const directLeafRows = [
      ...sourceGroup.directRows,
      ...subGroups.filter(([, group]) => !this.isFuelHierarchyLabel(group.label)).flatMap(([, group]) => group.rows),
    ];
    const subNodes = fuelSubGroups.map(([subLabel, subGroup]) =>
      this.buildHierarchySubNode(sectorLabel, sourceLabel, subLabel, subGroup),
    );
    const directLeafNodes = directLeafRows.map((row) => ({ row, children: [] }));
    const hasFuelGroups = subNodes.length > 0;
    const children = hasFuelGroups ? subNodes : directLeafNodes;
    const sourceRows = [
      ...directLeafNodes.map((node) => node.row),
      ...subNodes.flatMap((node) => node.children.map((child) => child.row)),
    ];
    return {
      row: this.buildAggregateRow(
        `source-total-${this.slugify(sectorLabel)}-${this.slugify(sourceLabel)}`,
        `Total ${sourceLabel}`,
        sourceRows,
        1,
        'source-total',
        hasFuelGroups || directLeafNodes.length > 0,
        hasFuelGroups,
      ),
      children,
    };
  }

  private buildHierarchySubNode(
    sectorLabel: string,
    sourceLabel: string,
    subLabel: string,
    subGroup: DailyReportHierarchySubGroup,
  ): DailyReportHierarchyNode {
    return {
      row: this.buildAggregateRow(
        `subcat-total-${this.slugify(sectorLabel)}-${this.slugify(sourceLabel)}-${this.slugify(subLabel)}`,
        `Total ${subLabel}`,
        subGroup.rows,
        2,
        'subcat-total',
        true,
        false,
      ),
      children: subGroup.rows.map((row) => ({ row, children: [] })),
    };
  }

  private buildAggregateRow(
    rowKey: string,
    label: string,
    rows: DailyReportRow[],
    rowLevel: number,
    rowKind: string,
    hasChildren: boolean,
    forceExpandChildren: boolean,
  ): DailyReportRow {
    const installedCapacity = rows.reduce((sum, row) => sum + (row.installed_capacity ?? 0), 0);
    const dailyValues = this.columns.reduce<Record<string, number | null>>((values, column) => {
      const total = rows.reduce((sum, row) => sum + (row.daily_values?.[column.key] ?? 0), 0);
      values[column.key] = Number(total.toFixed(5));
      return values;
    }, {});
    const installedCapacityLabel = this.formatNumber(Number(installedCapacity.toFixed(5)));
    return {
      station_id: 0,
      station_name: `${label} (${installedCapacityLabel})`,
      station_code: null,
      owner: null,
      source_category: null,
      sub_category: null,
      blocks: [],
      scheduled_mwh: 0,
      actual_mwh: 0,
      deviation_mwh: 0,
      installed_capacity: Number(installedCapacity.toFixed(5)),
      row_key: rowKey,
      row_kind: rowKind,
      row_level: rowLevel,
      has_children: hasChildren,
      force_expand_children: forceExpandChildren,
      daily_values: dailyValues,
    };
  }

  private getDefaultExpandedRowKeys(nodes: DailyReportHierarchyNode[]): Set<string> {
    const expandedKeys = new Set<string>();
    const visit = (items: DailyReportHierarchyNode[]): void => {
      for (const item of items) {
        if (item.row.force_expand_children && item.row.row_key) {
          expandedKeys.add(item.row.row_key);
        }
        if (item.children.length) {
          visit(item.children);
        }
      }
    };
    visit(nodes);
    return expandedKeys;
  }

  private normalizeHierarchyLabel(value: string | null | undefined): string {
    const candidate = typeof value === 'string' ? value.trim() : '';
    return candidate || 'Unassigned';
  }

  private normalizeOptionalHierarchyLabel(value: string | null | undefined): string | null {
    const candidate = typeof value === 'string' ? value.trim() : '';
    return candidate && !this.isMissingHierarchyLabel(candidate) ? candidate : null;
  }

  private isFuelHierarchyLabel(value: string | null | undefined): boolean {
    const candidate = typeof value === 'string' ? value.trim() : '';
    return Boolean(candidate && !this.isMissingHierarchyLabel(candidate));
  }

  private isMissingHierarchyLabel(value: string | null | undefined): boolean {
    const candidate = typeof value === 'string' ? value.trim().toLowerCase() : '';
    return candidate === '' || candidate === 'na' || candidate === 'n/a' || candidate === 'null' || candidate === '-';
  }

  private buildLinkedRowNodes(rows: DailyReportRow[]): DailyReportHierarchyNode[] {
    const nodeMap = new Map<string, DailyReportHierarchyNode>();
    const rootNodes: DailyReportHierarchyNode[] = [];
    for (const row of rows) {
      const rowKey = row.row_key ?? `${row.row_kind ?? 'data'}-${row.station_id}`;
      nodeMap.set(rowKey, { row: { ...row, row_key: rowKey }, children: [] });
    }

    for (const node of nodeMap.values()) {
      const parentNode = node.row.parent_row_key ? nodeMap.get(node.row.parent_row_key) : null;
      if (parentNode) {
        parentNode.children.push(node);
      } else {
        rootNodes.push(node);
      }
    }

    for (const node of nodeMap.values()) {
      if (node.children.length) {
        node.row.has_children = true;
        node.children = this.sortNodes(node.children);
      }
    }
    return rootNodes;
  }

  private flattenNodes(nodes: DailyReportHierarchyNode[]): DailyReportRow[] {
    const rows: DailyReportRow[] = [];
    for (const node of nodes) {
      rows.push(node.row);
      if (node.row.has_children && node.row.row_key && this.expandedRowKeys.has(node.row.row_key)) {
        rows.push(...this.flattenNodes(node.children));
      }
    }
    return rows;
  }

  private flattenAllNodes(nodes: DailyReportHierarchyNode[]): DailyReportRow[] {
    const rows: DailyReportRow[] = [];
    for (const node of nodes) {
      rows.push(node.row);
      rows.push(...this.flattenAllNodes(node.children));
    }
    return rows;
  }

  private sortNodes(nodes: DailyReportHierarchyNode[]): DailyReportHierarchyNode[] {
    return [...nodes].sort((left, right) => this.getNodeOrder(left) - this.getNodeOrder(right));
  }

  private getNodeOrder(node: DailyReportHierarchyNode): number {
    const key = this.getNodeOrderKey(node.row);
    return DAILY_SUMMARY_ACCORDION_ORDER_MAP.get(key) ?? DAILY_SUMMARY_ACCORDION_ORDER_MAP.size + 100;
  }

  private getNodeOrderKey(row: DailyReportRow): string {
    return getReportHierarchyNodeOrderKey(row);
  }

  private stripTotalLabel(value: string | null | undefined): string {
    return stripTotalReportLabel(value);
  }

  private stripTrailingParenthesizedText(value: string): string {
    return stripTrailingParenthesizedText(value);
  }

  private slugify(value: string): string {
    return slugifyReportLabel(value);
  }

  private formatNumber(value: number): string {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }

  private extractReport(response: unknown): DailyReportResponse | null {
    const value = this.extractResponseValue(response);
    if (!value) {
      return null;
    }
    const record = this.getRecord(value);
    return {
      data_date: this.extractString(record['data_date']) || getTodayDateValue(),
      date_label: this.extractString(record['date_label']) || '',
      section: (this.extractString(record['section']) as DailyReportTab) || this.selectedTab,
      columns: this.getArray(record['columns']).map((item) => {
        const column = this.getRecord(item);
        return {
          key: this.extractString(column['key']) || '',
          label: this.extractString(column['label']) || '',
        };
      }),
      rows: this.getArray(record['rows']).map((item) => this.extractRow(item)),
      summary: this.getRecord(record['summary']) as DailyReportResponse['summary'],
    };
  }

  private extractRow(value: unknown): DailyReportRow {
    const record = this.getRecord(value);
    return {
      station_id: this.normalizeNumber(record['station_id']) ?? 0,
      station_name: this.extractString(record['station_name']) || '-',
      station_code: this.extractString(record['station_code']),
      owner: this.extractString(record['owner']),
      source_category: this.extractString(record['source_category']),
      sub_category: this.extractString(record['sub_category']),
      blocks: this.getArray(record['blocks']) as DailyReportRow['blocks'],
      scheduled_mwh: this.normalizeNumber(record['scheduled_mwh']) ?? 0,
      actual_mwh: this.normalizeNumber(record['actual_mwh']) ?? 0,
      deviation_mwh: this.normalizeNumber(record['deviation_mwh']) ?? 0,
      installed_capacity: this.normalizeNumber(record['installed_capacity']),
      row_key: this.extractString(record['row_key']) || undefined,
      parent_row_key: this.extractString(record['parent_row_key']),
      row_kind: this.extractString(record['row_kind']),
      row_level: this.normalizeNumber(record['row_level']) ?? 0,
      has_children: Boolean(record['has_children']),
      force_expand_children: Boolean(record['force_expand_children']),
      force_flat: Boolean(record['force_flat']),
      is_small_power: Boolean(record['is_small_power']),
      daily_values: this.getRecord(record['daily_values']) as Record<string, number | null>,
    };
  }

  private extractResponseValue(response: unknown): unknown {
    if (!response || typeof response !== 'object') {
      return null;
    }

    return (response as { value?: unknown }).value ?? response;
  }

  private getRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
  }

  private getArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
  }

  private extractString(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  }

  private normalizeNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const numericValue = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }
}
